import React, { useEffect, useState } from 'react';
import { usePerformanceOptimizations } from '@/hooks/usePerformanceContext';

interface InstagramOptimizerProps {
  children: React.ReactNode;
}

/**
 * InstagramOptimizer - Componente que adapta a experiência para navegador Instagram
 * Implementa as diretrizes do documento INSTAGRAM_BROWSER_OPTIMIZATION.md
 */
export const InstagramOptimizer: React.FC<InstagramOptimizerProps> = ({ children }) => {
  const { isInstagram, shouldShowEscapeButton } = usePerformanceOptimizations();
  const [showEscapeButton, setShowEscapeButton] = useState(false);

  useEffect(() => {
    // Mostrar botão de escape após 3 segundos se for Instagram
    if (isInstagram && shouldShowEscapeButton) {
      const timer = setTimeout(() => {
        setShowEscapeButton(true);
      }, 3000);
      
      return () => clearTimeout(timer);
    }
  }, [isInstagram, shouldShowEscapeButton]);

  const handleEscapeToExternalBrowser = () => {
    // Tracking do evento
    if (typeof window !== 'undefined' && (window as unknown as Record<string, unknown>).gtag) {
      ((window as unknown as Record<string, (...args: unknown[]) => void>).gtag)('event', 'instagram_escape_attempt', {
        browser_type: 'instagram_webview',
        page_location: window.location.href
      });
    }

    // Tentar abrir em navegador externo
    const currentUrl = window.location.href;
    
    // Múltiplas estratégias para Instagram
    try {
      // Estratégia 1: Abrir nova janela/tab
      const newWindow = window.open(currentUrl, '_blank', 'noopener,noreferrer');
      
      // Estratégia 2: Se falhar, usar location
      if (!newWindow) {
        window.location.href = currentUrl;
      }
      
      // Estratégia 3: Criar link invisível e clicar
      setTimeout(() => {
        const link = document.createElement('a');
        link.href = currentUrl;
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      }, 500);
      
    } catch (error) {
      console.log('Instagram browser escape failed:', error);
    }
  };

  if (isInstagram) {
    return (
      <div className="instagram-optimized">
        {/* Banner de aviso para Instagram */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-3 text-center text-sm shadow-md relative z-50">
          <div className="flex items-center justify-center gap-2">
            <span className="text-base">📱</span>
            <span className="font-medium">
              Navegando pelo Instagram? Para melhor experiência:
            </span>
            <button 
              onClick={handleEscapeToExternalBrowser}
              className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded-full text-xs font-semibold transition-colors ml-2"
            >
              Abrir no Navegador
            </button>
          </div>
        </div>

        {/* Botão de escape flutuante (aparece após 3 segundos) */}
        {showEscapeButton && (
          <div className="fixed bottom-4 right-4 z-50 animate-bounce">
            <button
              onClick={handleEscapeToExternalBrowser}
              className="bg-blue-600 hover:bg-blue-700 text-white p-3 rounded-full shadow-lg transition-colors"
              title="Abrir no navegador para melhor experiência"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
              </svg>
            </button>
          </div>
        )}

        {/* Aplicar otimizações CSS específicas do Instagram */}
        <style dangerouslySetInnerHTML={{
          __html: `
            .instagram-optimized {
              /* Otimizações específicas para Instagram WebView */
              -webkit-overflow-scrolling: touch;
              overflow-scrolling: touch;
            }
            
            .instagram-optimized button,
            .instagram-optimized a {
              /* Melhorar área de toque */
              min-height: 44px;
              touch-action: manipulation;
            }
            
            .instagram-optimized input,
            .instagram-optimized textarea {
              /* Evitar zoom automático no Instagram */
              font-size: 16px !important;
              transform-origin: top left;
            }
            
            .instagram-optimized * {
              /* Simplificar animações no Instagram */
              animation-duration: 0.2s !important;
              transition-duration: 0.2s !important;
            }
            
            @media (prefers-reduced-motion: reduce) {
              .instagram-optimized * {
                animation: none !important;
                transition: none !important;
              }
            }
          `
        }} />

        {children}
      </div>
    );
  }

  // Render normal para navegadores padrão
  return <>{children}</>;
};

export default InstagramOptimizer;
